<script setup lang="ts">
import { Plus, Trash2 } from 'lucide-vue-next'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type {
  ExternalApiAuthConfig,
  ExternalApiAuthType,
  ExternalApiBasicAuthConfig,
  ExternalApiBearerAuthConfig,
  MarketingAction
} from '../types/marketingFlow'

type FieldErrors = string[] | null

const props = defineProps<{
  action: MarketingAction
  customVars: CustomVars[]
  urlErrors: FieldErrors
  methodErrors: FieldErrors
  headersErrors: FieldErrors
  authTypeErrors: FieldErrors
  authConfigErrors: FieldErrors
}>()

const emit = defineEmits<{
  update: [patch: Partial<MarketingAction>]
  bodyTemplateError: [hasError: boolean]
}>()

const { t } = useI18n()
const notification = useNotification()

const externalApiBodyTemplateLang = ref<'json' | 'xml'>('json')
const externalApiMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const externalApiAuthTypeOptions = computed(() => [
  {
    value: 'NONE' as ExternalApiAuthType,
    label: t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeNone')
  },
  {
    value: 'BEARER' as ExternalApiAuthType,
    label: t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeBearer')
  },
  {
    value: 'BASIC' as ExternalApiAuthType,
    label: t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeBasic')
  }
])

const getExternalApiAuthConfig = () => {
  if (!props.action.externalApiAuthConfig || typeof props.action.externalApiAuthConfig !== 'object') {
    return {}
  }

  return props.action.externalApiAuthConfig
}

const getExternalApiAuthConfigValue = (key: 'token' | 'username' | 'password'): string => {
  const config = getExternalApiAuthConfig()
  if ('token' in config) return key === 'token' ? (config as ExternalApiBearerAuthConfig).token : ''
  if ('username' in config) {
    const c = config as ExternalApiBasicAuthConfig
    return key === 'username' ? c.username : key === 'password' ? c.password : ''
  }
  return ''
}

const updateAction = (patch: Partial<MarketingAction>) => {
  emit('update', patch)
}

const updateExternalApiAuthType = (authType: ExternalApiAuthType) => {
  if (authType === 'NONE') {
    updateAction({
      externalApiAuthType: authType,
      externalApiAuthConfig: null,
      isExternalApiAuthConfigDirty: true,
      isExternalApiAuthConfigSet: false
    })
    return
  }

  let externalApiAuthConfig: MarketingAction['externalApiAuthConfig'] = null
  if (authType === 'BEARER') {
    externalApiAuthConfig = { token: '' }
  } else if (authType === 'BASIC') {
    externalApiAuthConfig = { username: '', password: '' }
  }

  updateAction({
    externalApiAuthType: authType,
    externalApiAuthConfig,
    isExternalApiAuthConfigDirty: true,
    isExternalApiAuthConfigSet: false
  })
}

const updateExternalApiAuthConfigField = (field: string, value: string) => {
  const currentConfig = getExternalApiAuthConfig()
  updateAction({
    externalApiAuthConfig: { ...currentConfig, [field]: value } as ExternalApiAuthConfig,
    isExternalApiAuthConfigDirty: true
  })
}

const addExternalApiHeader = () => {
  updateAction({
    externalApiHeaders: [...(props.action.externalApiHeaders ?? []), { key: '', value: '' }]
  })
}

const removeExternalApiHeader = (index: number) => {
  if (!props.action.externalApiHeaders) {
    return
  }

  const externalApiHeaders = props.action.externalApiHeaders.filter((_, headerIndex) => headerIndex !== index)
  updateAction({
    externalApiHeaders: externalApiHeaders.length > 0 ? externalApiHeaders : [{ key: '', value: '' }]
  })
}

const updateExternalApiHeader = (index: number, field: 'key' | 'value', value: string) => {
  updateAction({
    externalApiHeaders: (props.action.externalApiHeaders ?? []).map((header, headerIndex) =>
      headerIndex === index ? { ...header, [field]: value } : header
    )
  })
}

const authorizationHeaderIndex = computed(() =>
  (props.action.externalApiHeaders ?? []).findIndex((h) => h.key.trim().toLowerCase() === 'authorization')
)

const switchToBearer = () => {
  const idx = authorizationHeaderIndex.value
  if (idx === -1) return

  const rawValue = (props.action.externalApiHeaders ?? [])[idx]?.value.trim() ?? ''
  const bearerMatch = rawValue.match(/^bearer\s+(.+)$/i)

  if (!bearerMatch || !bearerMatch[1]?.trim()) {
    notification.create('error', {
      text: t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.switchToBearerUnrecognizedFormat')
    })
    return
  }

  const token = bearerMatch[1].trim()
  const headers = (props.action.externalApiHeaders ?? []).filter((_, i) => i !== idx)
  updateAction({
    externalApiHeaders: headers.length > 0 ? headers : [{ key: '', value: '' }],
    externalApiAuthType: 'BEARER',
    externalApiAuthConfig: { token },
    isExternalApiAuthConfigDirty: true,
    isExternalApiAuthConfigSet: false
  })
}

const getAuthConfigPlaceholder = () => {
  if (props.action.isExternalApiAuthConfigSet && !props.action.isExternalApiAuthConfigDirty) {
    return t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authConfigAlreadySet')
  }

  return ''
}
</script>

<template>
  <div class="mt-5 flex flex-col gap-5">
    <AppFormInput
      id="externalApiUrl"
      :model-value="action.externalApiUrl"
      :label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.urlLabel')"
      :placeholder="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.urlPlaceholder')"
      :errors="urlErrors"
      type="url"
      error-classes="text-xs"
      @update:model-value="(value) => updateAction({ externalApiUrl: String(value) })"
    />

    <Select
      :model-value="action.externalApiMethod || undefined"
      @update:model-value="
        (value) => {
          updateAction({ externalApiMethod: value as MarketingAction['externalApiMethod'] })
        }
      "
    >
      <SelectTrigger
        class="h-10 w-full"
        :aria-label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.methodLabel')"
      >
        <SelectValue
          :placeholder="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.methodPlaceholder')"
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="method in externalApiMethods" :key="method" :value="method">
          {{ method }}
        </SelectItem>
      </SelectContent>
    </Select>
    <span v-if="methodErrors" class="text-xs text-red-600">{{ methodErrors[0] }}</span>

    <div class="flex flex-col gap-3">
      <div class="text-sm font-medium text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.headersLabel') }}
      </div>
      <div
        v-for="(header, headerIndex) in action.externalApiHeaders"
        :key="`header-${headerIndex}`"
        class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]"
      >
        <Input
          :model-value="header.key"
          :aria-label="`${t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.headerKeyPlaceholder')} ${headerIndex + 1}`"
          :placeholder="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.headerKeyPlaceholder')"
          @update:model-value="(value) => updateExternalApiHeader(headerIndex, 'key', String(value))"
        />
        <Input
          :model-value="header.value"
          :aria-label="`${t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.headerValuePlaceholder')} ${headerIndex + 1}`"
          :placeholder="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.headerValuePlaceholder')"
          @update:model-value="(value) => updateExternalApiHeader(headerIndex, 'value', String(value))"
        />
        <Button
          variant="outline-destructive"
          size="icon-sm"
          :aria-label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.removeHeader')"
          @click="removeExternalApiHeader(headerIndex)"
        >
          <Trash2 />
        </Button>
      </div>
      <Button variant="outline" class="w-fit" @click="addExternalApiHeader">
        <Plus />
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.addHeader') }}
      </Button>
      <span v-if="headersErrors" class="text-xs text-red-600">{{ headersErrors[0] }}</span>
      <BaseTip
        v-if="authorizationHeaderIndex >= 0"
        severity="warn"
        :text="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authorizationHeaderWarning')"
      >
        <template #button>
          <Button variant="outline" size="sm" @click="switchToBearer">
            {{ t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.switchToBearer') }}
          </Button>
        </template>
      </BaseTip>
    </div>

    <div class="flex flex-col gap-3">
      <CodeEditorWithVars
        :model-value="action.externalApiBodyTemplate || ''"
        :label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.bodyLabel')"
        :lang="externalApiBodyTemplateLang"
        :custom-vars="customVars"
        @update:model-value="(value) => updateAction({ externalApiBodyTemplate: value })"
        @update:lang="(value) => (externalApiBodyTemplateLang = value)"
        @has-error="(val) => emit('bodyTemplateError', val)"
      />
    </div>

    <div class="flex flex-col gap-3">
      <div class="text-sm font-medium text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authenticationSummary') }}
      </div>

      <Select
        :model-value="action.externalApiAuthType || 'NONE'"
        @update:model-value="(value: ExternalApiAuthType) => updateExternalApiAuthType(value)"
      >
        <SelectTrigger
          class="h-10 w-full"
          :aria-label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeLabel')"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="authOption in externalApiAuthTypeOptions"
            :key="authOption.value"
            :value="authOption.value"
          >
            {{ authOption.label }}
          </SelectItem>
        </SelectContent>
      </Select>
      <p class="text-xs text-muted-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeHelp') }}
      </p>
      <span v-if="authTypeErrors" class="text-xs text-red-600">{{ authTypeErrors[0] }}</span>

      <div v-if="action.externalApiAuthType === 'BEARER'" class="grid grid-cols-1 gap-2">
        <AppFormInput
          id="externalApiBearerToken"
          :model-value="getExternalApiAuthConfigValue('token')"
          :label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.bearerTokenLabel')"
          :placeholder="getAuthConfigPlaceholder()"
          type="password"
          @update:model-value="(value) => updateExternalApiAuthConfigField('token', String(value))"
        />
      </div>

      <div v-if="action.externalApiAuthType === 'BASIC'" class="grid grid-cols-2 gap-2">
        <AppFormInput
          id="externalApiBasicUsername"
          :model-value="getExternalApiAuthConfigValue('username')"
          :label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.basicUsernameLabel')"
          :placeholder="getAuthConfigPlaceholder()"
          @update:model-value="(value) => updateExternalApiAuthConfigField('username', String(value))"
        />
        <AppFormInput
          id="externalApiBasicPassword"
          :model-value="getExternalApiAuthConfigValue('password')"
          :label="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.basicPasswordLabel')"
          :placeholder="getAuthConfigPlaceholder()"
          type="password"
          @update:model-value="(value) => updateExternalApiAuthConfigField('password', String(value))"
        />
      </div>

      <span v-if="authConfigErrors" class="text-xs text-red-600">{{ authConfigErrors[0] }}</span>
    </div>
  </div>
</template>
